import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Popover,
  MenuList,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Business,
  Home,
  Add,
  MoreVert,
  Visibility,
  Delete,
  CalendarViewDay,
  CalendarMonth,
  LocationOn,
  Person,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Property, PropertyCreateData, propertyApi, unitApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { countries, getPhoneCodeByCountryName, Country } from '../utils/countries';
import { getStorageUrl, getApiUrl } from '../config/api';
import { MapLocationPicker } from '../components/MapLocationPicker';

const propertySchema = yup.object({
  name: yup.string().required('Property name is required'),
  type: yup.string().oneOf(['apartment', 'house', 'condo', 'commercial', 'building', 'villa', 'other']).required('Property type is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().notRequired(),
  country: yup.string().notRequired(),
  currency_id: yup.number().notRequired(),
  description: yup.string().notRequired(),
  mobile_phone_1: yup.string().notRequired(),
  mobile_phone_2: yup.string().notRequired(),
  facilities: yup.array().of(yup.string()).notRequired(),
  special_specs: yup.array().of(yup.string()).notRequired(),
  showOnCalendar: yup.boolean().notRequired(),
});

interface PropertyFormData {
  name: string;
  type: 'apartment' | 'house' | 'condo' | 'commercial' | 'building' | 'villa' | 'other';
  address: string;
  city?: string;
  country?: string;
  currency_id?: number;
  description?: string;
  mobile_phone_1?: string;
  mobile_phone_2?: string;
  facilities?: string[];
  special_specs?: string[];
}

// Custom component for managing string arrays with chips
interface FacilitiesInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
}

const FacilitiesInput: React.FC<FacilitiesInputProps> = ({ value, onChange, label }) => {
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      if (!value.includes(inputValue.trim())) {
        onChange([...value, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  const handleDelete = (indexToDelete: number) => {
    onChange(value.filter((_, index) => index !== indexToDelete));
  };

  return (
    <Box>
      <TextField
        fullWidth
        label={label}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyPress={handleKeyPress}
        helperText={t('press_enter_to_add')}
      />
      {value.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          {value.map((item, index) => (
            <Chip
              key={index}
              label={item}
              onDelete={() => handleDelete(index)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export const PropertiesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = user?.role === 'admin';
  const isPropertyManager = user?.role === 'property_manager';
  const canCreateProperties = isAdmin || isPropertyManager;
  const { t } = useTranslation();

  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
      const [editingProperty, setEditingProperty] = useState<Property | null>(null);
    const [error, setError] = useState<string>('');
    const [mapDialogOpen, setMapDialogOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PropertyCreateData>({
    // resolver: yupResolver(propertySchema), // Temporarily disabled due to TypeScript issues
    defaultValues: {
      name: '',
      type: 'apartment',
      address: '',
      city: '',
      country: '',
      currency_id: undefined,
      latitude: undefined,
      longitude: undefined,
      description: '',
      mobile_phone_1: '',
      mobile_phone_2: '',
      facilities: [],
      special_specs: [],
      showOnCalendar: true,
    },
  });

  // Fetch currencies for currency selection
  const { data: currencies = [], isLoading: isLoadingCurrencies, error: currenciesError } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      console.log('Fetching currencies in PropertiesPage...');
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      // Ensure we always return an array
      const currenciesArray = Array.isArray(data) ? data : (data.data || []);
      console.log('Currencies fetched in PropertiesPage:', currenciesArray);
      return currenciesArray;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    console.log('PropertiesPage component mounted, fetching properties...');
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      console.log('Fetching properties...');
      setLoading(true);
      const response = await propertyApi.getAll();
      console.log('Properties fetched successfully:', response);
      setProperties(response);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError(t('failed_fetch_properties'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (property?: Property) => {
    if (property) {
      setEditingProperty(property);
      reset({
        name: property.name,
        type: property.type,
        address: property.address,
        city: property.city || '',
        country: property.country || '',
        currency_id: property.currency_id || undefined,
        latitude: property.latitude,
        longitude: property.longitude,
        description: property.description || '',
        mobile_phone_1: property.mobile_phone_1 || '',
        mobile_phone_2: property.mobile_phone_2 || '',
        facilities: property.facilities || [],
        special_specs: property.special_specs || [],
        showOnCalendar: property.showOnCalendar ?? true,
      });
    } else {
      setEditingProperty(null);
      reset();
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProperty(null);
    reset();
    setError('');
  };

  const onSubmit = async (data: PropertyCreateData) => {
    try {
      console.log('Submitting property data:', data);
      // Use the data as-is since currency_id is already a number
      const propertyData = { ...data };
      
      if (editingProperty) {
        console.log('Updating property:', editingProperty.id);
        await propertyApi.update(editingProperty.id, propertyData);
      } else {
        console.log('Creating new property');
        const createdProperty = await propertyApi.create(propertyData);
        
        // If the property type is "house" (Single Unit), automatically create a unit with name "Main"
        if (propertyData.type === 'house') {
          console.log('Creating default unit for Single Unit property');
          try {
            await unitApi.create({
              property_id: createdProperty.id,
              unit_number: '1',
              type: 'apartment', // Default unit type
              name: 'Main',
              bedrooms: 1, // Default value
              bathrooms: 1, // Default value
              square_feet: 0, // Default value
              rent_amount: 0, // Default value
              status: 'vacant',
              cleaning_status: 'cleaned',
              description: 'Main unit for single unit property',
              rental_types: ['Daily', 'Monthly'], // Default rental types
              living_room_count: 1, // Default value
              mens_guest_room_count: 0, // Default value
              womens_guest_room_count: 0, // Default value
              single_bed_count: 1, // Default value
              double_bed_count: 0, // Default value
              max_persons: 2, // Default value
              checkin_time: '14:00', // Default value
              checkout_time: '12:00', // Default value
              tax_rate: 0, // Default value
            });
            console.log('Default unit created successfully');
          } catch (unitError) {
            console.error('Error creating default unit:', unitError);
            // Don't fail the property creation if unit creation fails
          }
        }
      }
      await fetchProperties();
      handleCloseDialog();
      console.log('Property saved successfully');
    } catch (err: any) {
      console.error('Error saving property:', err);
      setError(err.response?.data?.message || t('failed_save_property'));
    }
  };

  const handleLocationSelect = (latitude: number, longitude: number, address?: string) => {
    // Update the form with the selected coordinates
    const currentValues = watch();
    reset({
      ...currentValues,
      latitude,
      longitude,
      address: address || currentValues.address,
    });
  };



  const PropertyCard: React.FC<{ property: Property }> = ({ property }) => {
    const [cardAnchorEl, setCardAnchorEl] = useState<null | HTMLElement>(null);
    
    const getMainImageUrl = (imagePath?: string) => {
      if (!imagePath) return null;
      // If the path already includes the full URL, return as-is
      if (imagePath.startsWith('http')) return imagePath;
      // Otherwise, construct the full URL using centralized config
      return getStorageUrl(imagePath);
    };

    const mainImageUrl = getMainImageUrl(property.main_image);
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(!!mainImageUrl);
    
    const shouldShowImage = mainImageUrl && !imageError && !imageLoading;

    const handleCardMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      setCardAnchorEl(event.currentTarget);
    };

    const handleCardMenuClose = () => {
      setCardAnchorEl(null);
    };

    const handleCardDelete = async () => {
      try {
        await propertyApi.delete(property.id);
        await fetchProperties();
        handleCardMenuClose();
      } catch (error: any) {
        console.error('Error deleting property:', error);
        // Handle specific error types from backend
        if (error.response?.status === 422) {
          const errorData = error.response.data;
          let errorMessage = errorData.message || 'Cannot delete property';
          
          // Add specific details based on error type
          if (errorData.error === 'has_units') {
            errorMessage += `. Please delete all units first (${errorData.unit_count} unit(s) found).`;
          } else if (errorData.error === 'has_accounting_transactions') {
            errorMessage += `. Please delete all accounting transactions first (${errorData.transaction_count} transaction(s) found).`;
          } else if (errorData.error === 'has_purchase_invoices') {
            errorMessage += `. Please delete all purchase invoices first (${errorData.invoice_count} invoice(s) found).`;
          } else if (errorData.error === 'has_bookings') {
            errorMessage += `. Please delete all bookings first (${errorData.booking_count} booking(s) found).`;
          }
          
          setError(errorMessage);
        } else {
          setError(error.response?.data?.message || 'Failed to delete property');
        }
      }
    };

    const handleCardToggleShowOnCalendar = async () => {
      try {
        const newShowOnCalendar = !property.showOnCalendar;
        await propertyApi.toggleShowOnCalendar(property.id, newShowOnCalendar);
        await fetchProperties();
        handleCardMenuClose();
      } catch (error) {
        console.error('Error toggling calendar visibility:', error);
      }
    };

    return (
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Box
          sx={{
            height: 200,
            backgroundImage: shouldShowImage 
              ? `url(${mainImageUrl})`
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => navigate(`/properties/${property.id}`)}
        >
          {/* Overlay for better text visibility when image is present */}
          {shouldShowImage && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                zIndex: 1,
              }}
            />
          )}
          
          {/* Show business icon and property name overlay when no image */}
          {!shouldShowImage && (
            <>
              <Business sx={{ fontSize: 48, color: 'white', opacity: 0.7 }} />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    fontSize: '1rem',
                  }}
                >
                  {property.name}
                </Typography>
              </Box>
            </>
          )}
          
          {/* Hidden image element for error handling */}
          {mainImageUrl && (
            <img
              src={mainImageUrl}
              alt={property.name}
              style={{ display: 'none' }}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
              onLoad={() => {
                setImageError(false);
                setImageLoading(false);
              }}
            />
          )}
          
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
            }}
          >
            <Chip
              label={property.type}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'text.primary',
                fontWeight: 'bold',
                textTransform: 'capitalize',
              }}
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
            }}
          >
            <IconButton
              size="small"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCardMenuClick(e);
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {property.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {property.address}
              {property.city && `, ${property.city}`}
              {property.country && `, ${property.country}`}
            </Typography>
          </Box>
          {property.creator && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Person fontSize="small" color="action" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Created by {property.creator.name}
              </Typography>
            </Box>
          )}
          {property.facilities && property.facilities.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                {t('facilities_label')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {property.facilities.slice(0, 3).map((facility, index) => (
                  <Chip
                    key={index}
                    label={facility}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
                {property.facilities.length > 3 && (
                  <Chip
                    label={`+${property.facilities.length - 3} ${t('more_facilities')}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          )}
          {property.description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {property.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Home fontSize="small" color="action" />
              <Typography variant="body2">
                {property.units?.length || 0} {t('units').toLowerCase()}
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<Visibility />}
              onClick={() => navigate(`/properties/${property.id}`)}
            >
              {t('view_details')}
            </Button>
          </Box>
        </CardContent>

        {/* Property Action Menu */}
        <Popover
          anchorEl={cardAnchorEl}
          open={Boolean(cardAnchorEl)}
          onClose={handleCardMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              elevation: 8,
              sx: {
                mt: 0.5,
                minWidth: 150,
                maxWidth: 200,
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 12,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            },
          }}
        >
          <MenuList dense>
            <MenuItem onClick={() => {
              navigate(`/properties/${property.id}`);
              handleCardMenuClose();
            }}>
              <Visibility sx={{ mr: 1 }} fontSize="small" />
              {t('view_details')}
            </MenuItem>
            {(canCreateProperties || isAdmin) && (
              <MenuItem onClick={handleCardToggleShowOnCalendar}>
                {property.showOnCalendar ? (
                  <CalendarViewDay sx={{ mr: 1 }} fontSize="small" />
                ) : (
                  <CalendarMonth sx={{ mr: 1 }} fontSize="small" />
                )}
                {property.showOnCalendar 
                  ? t('hide_from_calendar') 
                  : t('show_on_calendar')}
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={handleCardDelete} sx={{ color: 'error.main' }}>
                <Delete sx={{ mr: 1 }} fontSize="small" />
                {t('delete')}
              </MenuItem>
            )}
          </MenuList>
        </Popover>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('properties')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {t('manage_property_portfolio')}
          </Typography>
        </Box>
        {canCreateProperties && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            {t('add_property')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Properties Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" height={20} />
                  <Skeleton variant="text" width="60%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : properties.length > 0 ? (
        <Grid container spacing={3}>
          {properties.map((property) => (
            <Grid item xs={12} sm={6} md={4} key={property.id}>
              <PropertyCard property={property} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <Business sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('no_properties_yet')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {canCreateProperties
              ? t('create_first_property')
              : t('ask_admin_assign_properties')}
          </Typography>
          {canCreateProperties && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              {t('add_your_first_property')}
            </Button>
          )}
        </Box>
      )}

      {/* Mobile FAB */}
      {canCreateProperties && isMobile && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleOpenDialog()}
        >
          <Add />
        </Fab>
      )}



      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingProperty ? t('edit_property') : t('add_property')}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('property_name')}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>{t('property_type')}</InputLabel>
                      <Select {...field} label={t('property_type')}>
                        <MenuItem value="apartment">{t('property_type_apartment')}</MenuItem>
                        <MenuItem value="house">{t('property_type_house')}</MenuItem>
                        <MenuItem value="condo">{t('property_type_condo')}</MenuItem>
                        <MenuItem value="commercial">{t('property_type_commercial')}</MenuItem>
                        <MenuItem value="building">{t('property_type_building')}</MenuItem>
                        <MenuItem value="villa">{t('property_type_villa')}</MenuItem>
                        <MenuItem value="other">{t('property_type_other')}</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="currency_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>{t('currency')}</InputLabel>
                      <Select
                        {...field}
                        label={t('currency')}
                        value={field.value?.toString() || ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value as string) : undefined)}
                      >
                        <MenuItem value="">
                          <em>{t('select_currency')}</em>
                        </MenuItem>
                        {Array.isArray(currencies) ? currencies
                          .filter((currency: any) => currency.is_active)
                          .map((currency: any) => (
                          <MenuItem key={currency.id} value={currency.id}>
                            {currency.code} - {currency.name} ({currency.symbol})
                          </MenuItem>
                        )) : null}
                      </Select>
                      {currenciesError && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                          {t('failed_load_currencies')}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('address')}
                      multiline
                      rows={2}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('city')}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.country}>
                      <InputLabel>{t('country')}</InputLabel>
                      <Select
                        {...field}
                        label={t('country')}
                      >
                        <MenuItem value="">
                          <em>{t('select_country')}</em>
                        </MenuItem>
                        {countries.map((country: Country) => (
                          <MenuItem key={country.code} value={country.name}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.country && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                          {errors.country.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              
              {/* Location Section */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {t('location')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<LocationOn />}
                    onClick={() => setMapDialogOpen(true)}
                  >
                    {t('select_on_map')}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="latitude"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('latitude')}
                      type="number"
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText={t('latitude')}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="longitude"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('longitude')}
                      type="number"
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText={t('longitude')}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="mobile_phone_1"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('mobile_phone_1')}
                      error={!!errors.mobile_phone_1}
                      helperText={errors.mobile_phone_1?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box sx={{ 
                              fontWeight: 'bold', 
                              color: 'primary.main',
                              bgcolor: 'grey.100',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.875rem'
                            }}>
                              {getPhoneCodeByCountryName(watch('country') || '') || '+XX'}
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="mobile_phone_2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('mobile_phone_2')}
                      error={!!errors.mobile_phone_2}
                      helperText={errors.mobile_phone_2?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box sx={{ 
                              fontWeight: 'bold', 
                              color: 'primary.main',
                              bgcolor: 'grey.100',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.875rem'
                            }}>
                              {getPhoneCodeByCountryName(watch('country') || '') || '+XX'}
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="facilities"
                  control={control}
                  render={({ field }) => (
                    <FacilitiesInput
                      value={field.value || []}
                      onChange={field.onChange}
                      label={t('facilities')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="special_specs"
                  control={control}
                  render={({ field }) => (
                    <FacilitiesInput
                      value={field.value || []}
                      onChange={field.onChange}
                      label={t('special_specifications')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('description')}
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="showOnCalendar"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value ?? true}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('show_on_calendar')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('saving') : editingProperty ? t('update') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Location Picker Dialog */}
      <MapLocationPicker
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        onLocationSelect={handleLocationSelect}
        initialLatitude={watch('latitude')}
        initialLongitude={watch('longitude')}
        initialAddress={watch('address')}
      />
    </Box>
  );
};
